import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Layout from '../components/Layout';
import SummaryCard from '../components/SummaryCard';
import StatusBadge from '../components/StatusBadge';
import { getDashboard, getAllocations } from '../services/api';
import { useToken } from '../hooks/useToken';
import type { DashboardData, PaymentRequest } from '../types';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return new Date(s.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PAGE_SIZE = 10;

export default function Dashboard() {
  const getToken = useToken();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [data, setData] = useState<DashboardData | null>(null);
  const [remainingBudget, setRemainingBudget] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!accounts[0]) return;
    getToken()
      .then(async (t) => {
        const dash = await getDashboard(t);
        setData(dash);
        const fiscalYearId = dash.fiscal_year?.fiscal_year_id;
        if (fiscalYearId) {
          const allocs = await getAllocations(t, { fiscalYearId });
          const remaining = allocs.data.reduce((sum, a) => sum + Number(a.remaining_balance), 0);
          setRemainingBudget(remaining);
        }
      })
      .catch((e: Error) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts[0]?.localAccountId]);

  if (error) {
    return (
      <Layout pageTitle="Dashboard">
        <p className="text-red-600 text-sm">{error}</p>
      </Layout>
    );
  }

  const stats = data?.stats;
  const fyLabel = data?.fiscal_year?.label ?? '';
  const allPending = data?.recent_activity ?? [];
  const totalPages = Math.ceil(allPending.length / PAGE_SIZE);
  const pageRows = allPending.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Layout pageTitle="Dashboard">
      <p className="text-sm text-gray-500 mb-5">
        Payment request activity for BHSD fiscal year{fyLabel ? ` ${fyLabel}` : ''}
      </p>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          label="Total Pending Amount"
          value={stats ? formatCurrency(stats.pending_amount + stats.pending_payment_amount ) : '—'}
        />
        <SummaryCard
          label="Remaining Budget"
          value={remainingBudget !== null ? formatCurrency(remainingBudget) : '—'}
        />
        <SummaryCard
          label="Requests Pending Approval"
          value={stats ? stats.pending_count : '—'}
        />
        <SummaryCard
          label="Paid (Last 30 Days)"
          value={stats ? stats.paid_last_30_days_count : '—'}
        />
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Pending Transactions</h2>
          <button
            onClick={() => navigate('/requests')}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            View All Requests
          </button>
        </div>
        <ActivityTable
          rows={pageRows}
          loading={!data}
          onRowClick={(id) => navigate(`/requests/${id}`)}
        />
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, allPending.length)} of {allPending.length}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
                className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function ActivityTable({
  rows,
  loading,
  onRowClick,
}: {
  rows: PaymentRequest[];
  loading: boolean;
  onRowClick: (id: number) => void;
}) {
  if (loading) {
    return <div className="px-5 py-8 text-center text-sm text-gray-400">Loading...</div>;
  }
  if (rows.length === 0) {
    return <div className="px-5 py-8 text-center text-sm text-gray-400">No pending transactions.</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Request #</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Payee</th>
          <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fund Pool</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fiscal Year</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">BHSD Approval Date</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.payment_request_id}
            onClick={() => onRowClick(r.payment_request_id)}
            className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer last:border-0"
          >
            <td className="px-5 py-3 font-mono text-xs text-gray-700">{r.request_number}</td>
            <td className="px-5 py-3 text-gray-900">{r.payee_name}</td>
            <td className="px-5 py-3 text-right text-gray-900">{formatCurrency(r.amount)}</td>
            <td className="px-5 py-3 text-gray-700 font-mono text-xs">{r.fund_pool_code}</td>
            <td className="px-5 py-3 text-gray-700">{r.fiscal_year_label}</td>
            <td className="px-5 py-3"><StatusBadge status={r.status_name} /></td>
            <td className="px-5 py-3 text-gray-500">{formatDate(r.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
